/**
 * The OAuth `state` correlator for social sign-in, and the decision the callback page makes
 * before it exchanges anything.
 *
 * ## What `state` is for here
 *
 * `buildSocialLoginUrl` mints `initCode = base64url({nonce, redirectUri})` and sends it to
 * Cognito as the OAuth `state` parameter. Cognito echoes `state` back on the callback
 * unchanged. So the value in the URL after the round trip is exactly the value this browser
 * parked before it started — which makes it the standard OAuth CSRF correlator, and comparing
 * the two is the only thing that ties the code in the URL to a sign-in *this* browser began.
 *
 * ## The attack this closes
 *
 * Without the comparison, the callback page exchanges whatever `?code=` it is handed against
 * whatever `initCode` it has parked. An attacker completes `/oauth2/authorize` for their own
 * account against the same app client and redirect URI, keeps the resulting `code`, and sends
 * a victim who has a pending social login to `…/auth/callback?code=<attacker-code>`. The
 * victim's browser exchanges the attacker's code, stores the attacker's session token, and the
 * victim is silently operating inside someone else's account — every action they take, every
 * document they upload, lands there.
 *
 * Requiring `state` to byte-match the parked `initCode` closes it: the attacker cannot read
 * the victim's parked nonce (it is per-origin storage in the victim's browser), so a link they
 * craft either carries their own `state` (mismatch) or none (absent). Both are refused.
 *
 * ## Fail closed, and single use
 *
 * A missing `state` is refused rather than waved through. An OAuth server that was given
 * `state` must return it; one that does not is either not the server we asked, or the value
 * was stripped in transit — and "no correlator" is exactly the state the attack above needs.
 *
 * The parked blob is consumed on read (see {@link takePendingSocialLogin}), so a callback URL
 * that is replayed — reloaded, bookmarked, or re-sent by an attacker after a successful
 * sign-in — finds nothing parked and is refused. A user whose exchange genuinely failed
 * restarts from the sign-in button, which is one click and mints a fresh nonce.
 *
 * ## Why sessionStorage and not localStorage
 *
 * The nonce is bound to the browsing session that started the flow. `sessionStorage` is scoped
 * to the tab, survives the top-level navigation to the IdP and back (same tab, same origin),
 * and is gone when the tab closes. `localStorage` would leave a usable correlator parked for
 * every other tab and for every later visit — including one an abandoned flow left behind
 * weeks ago.
 */
/**
 * Storage key under which the in-flight social-login state is parked. Exported so projects can
 * clear it manually if they need to reset a wedged flow.
 */
export const SOCIAL_LOGIN_PENDING_KEY = 'auth.pendingSocialLogin';
/**
 * Returns the tab-scoped store the pending state lives in, or `null` when there isn't one
 * (server-side rendering, or a browser configured to refuse storage). A null store is not
 * routed around: with nowhere to park a nonce there is no way to verify one, and starting a
 * flow that cannot be verified is the thing this module exists to prevent.
 */
function socialLoginStore() {
    if (typeof window === 'undefined')
        return null;
    try {
        return window.sessionStorage;
    }
    catch {
        return null;
    }
}
/**
 * Parks the pending state for the tab. Returns false when there is nowhere to park it, so the
 * caller can refuse to start the flow instead of redirecting into a callback that will fail.
 */
export function writePendingSocialLogin(pending) {
    const store = socialLoginStore();
    if (store === null)
        return false;
    try {
        store.setItem(SOCIAL_LOGIN_PENDING_KEY, JSON.stringify(pending));
        return true;
    }
    catch {
        return false;
    }
}
/**
 * Reads the parked state **and removes it**, so it is good for exactly one callback. Returns
 * null when nothing is parked or the blob is unusable.
 */
export function takePendingSocialLogin() {
    const store = socialLoginStore();
    if (store === null)
        return null;
    let raw = null;
    try {
        raw = store.getItem(SOCIAL_LOGIN_PENDING_KEY);
        store.removeItem(SOCIAL_LOGIN_PENDING_KEY);
    }
    catch {
        return null;
    }
    if (raw === null)
        return null;
    try {
        const parsed = JSON.parse(raw);
        if (typeof parsed.initCode !== 'string' || parsed.initCode.length === 0)
            return null;
        return {
            initCode: parsed.initCode,
            next: typeof parsed.next === 'string' ? parsed.next : '/',
            ...(typeof parsed.provider === 'string' ? { provider: parsed.provider } : {}),
            ...(parsed.mode === 'login' || parsed.mode === 'signup' ? { mode: parsed.mode } : {}),
        };
    }
    catch {
        return null;
    }
}
/** Drops any parked state without reading it. Used when a flow is abandoned mid-way. */
export function clearPendingSocialLogin() {
    const store = socialLoginStore();
    if (store === null)
        return;
    try {
        store.removeItem(SOCIAL_LOGIN_PENDING_KEY);
    }
    catch {
        // Nothing to do — the worst case is a stale blob the next sign-in overwrites.
    }
}
/**
 * Auth-internal paths a successful sign-in must never navigate back to. A previous failed
 * attempt can park one here (e.g. `/auth/callback?code=stale`); landing on it after the
 * exchange re-renders the callback page with no usable URL params and shows "no pending
 * state" to a user who just signed in successfully.
 */
const AUTH_SURFACE_PATH = /^\/(login|signup|recovery|verification|mfa|auth)\b/;
/**
 * Decides what the callback page should do, given the URL the IdP returned to and the state
 * this browser parked. Pure — no storage, no network, no React — because every branch below is
 * a security decision and a security decision that can only be exercised through a mounted
 * component with a stubbed DOM is a security decision nobody tests.
 */
export function resolveSocialCallback(input) {
    let url;
    try {
        url = new URL(input.currentUrl);
    }
    catch {
        return { kind: 'error', message: 'The OIDC callback URL could not be parsed.' };
    }
    const returnToCode = url.searchParams.get('code');
    const stateInUrl = url.searchParams.get('state');
    const flowIdInUrl = url.searchParams.get('flow');
    // The IdP came back with `?flow=<id>` and no `?code=`: the OIDC result wasn't directly
    // usable, most commonly because the email matches an existing local identity and the user
    // has to confirm ownership before the credentials are linked. No exchange happens on this
    // branch — the link page runs its own flow and returns its own session token — so there is
    // no code to bind and nothing for `state` to protect.
    if (returnToCode === null && flowIdInUrl !== null) {
        return { kind: 'link-probe', flowId: flowIdInUrl };
    }
    if (returnToCode === null) {
        return { kind: 'error', message: 'The OIDC callback URL is missing the `code` query parameter.' };
    }
    if (input.pending === null) {
        return {
            kind: 'error',
            message: 'No pending social-login state was found in this browser. Try signing in again.',
        };
    }
    // The CSRF check. Absent and mismatched are separate messages because they are separate
    // operational problems: absent usually means the `state` was stripped between Cognito and
    // here (a proxy, a rewrite rule), while a mismatch means this browser is being handed a code
    // from a sign-in it did not start.
    if (stateInUrl === null || stateInUrl.length === 0) {
        return {
            kind: 'error',
            message: 'The OIDC callback did not return the `state` value this browser sent, so the sign-in could not be verified. Try signing in again.',
        };
    }
    if (stateInUrl !== input.pending.initCode) {
        return {
            kind: 'error',
            message: 'This sign-in did not start in this browser, so it was not completed. Try signing in again.',
        };
    }
    const rawNext = input.pending.next.length > 0 ? input.pending.next : '/';
    return {
        kind: 'exchange',
        initCode: input.pending.initCode,
        returnToCode,
        mode: input.pending.mode ?? 'login',
        next: AUTH_SURFACE_PATH.test(rawNext) ? '/' : rawNext,
    };
}
//# sourceMappingURL=social-login-state.js.map